import { Flex, chakra } from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Dropdown,
  DropdownButton,
  DropdownContent,
  DropdownItem,
  IconButton,
  ModalBody,
  ModalFooterDefault,
  ModalHeaderDefault,
  PlusIcon,
  SavedList,
  SavedListToggleItemHandler,
  SearchInput,
  TrashIcon,
} from 'ui-components';

import {
  PAGE_SIZE,
  STEP,
  TYPE,
  TYPE_NAME,
} from '../SavedListsModalV2.constants';
import { useSavedListsModalProvider } from '../SavedListsModalV2.context';
import { useSavedListsBodyHeight } from '../SavedListsModalV2.hooks';
import { useSavedListStateStore } from '../SavedListsModalV2.store';
import { EmptyResults } from './EmptyResults';

export const StepOneSelectLists: React.FC = () => {
  const {
    clearList,
    deleteList,
    lists: state,
    updateList,
  } = useSavedListStateStore();
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const modalHeaderRef = useRef<HTMLDivElement>(null);
  const modalFooterRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);

  const [checkedIds, setCheckedIds] = useState<(string | number)[]>(
    Object.keys(state),
  );

  useEffect(() => setCheckedIds(Object.keys(state)), [state]);

  const {
    creators,
    fetchNextPage,
    hasNextPage,
    isInitialLoading,
    isLoading,
    isSubmittingCreateList,
    listQuery,
    lists,
    onClose,
    onSubmitCreateList,
    renderCreateItem,
    setListQuery,
    setRenderCreateItem,
    setStep,
    setType,
    total,
    type,
  } = useSavedListsModalProvider();

  const handleListToggle: SavedListToggleItemHandler = useCallback(
    (list, updatedCheckedIds) => {
      const isChecked = updatedCheckedIds.includes(list.id);

      setCheckedIds(updatedCheckedIds);

      if (!isChecked) {
        return deleteList(list.id);
      }

      return updateList(list.id, {
        creators,
        name: list.name,
      });
    },
    [deleteList, creators, updateList],
  );

  const createItemOptions = {
    isSubmitting: isSubmittingCreateList,
    onCancel: () => setRenderCreateItem(false),
    onSubmit: onSubmitCreateList,
  };

  const maxListHeight = useSavedListsBodyHeight({
    modalBodyRef,
    toolbarRef,
  });

  const handleTypeChange = useCallback(
    (newType: TYPE) => {
      setType(newType);
      setListQuery('');
    },
    [setType, setListQuery],
  );

  return (
    <>
      <ModalHeaderDefault title="Select lists" ref={modalHeaderRef} />

      <ModalBody ref={modalBodyRef}>
        <Flex
          alignItems="center"
          flexWrap="wrap"
          gap={2}
          rowGap={6}
          marginBottom={6}
          ref={toolbarRef}
        >
          <Dropdown>
            <DropdownButton>{TYPE_NAME[type]}</DropdownButton>
            <DropdownContent>
              <DropdownItem
                isActive={type === TYPE.agency}
                onClick={() => handleTypeChange(TYPE.agency)}
              >
                {TYPE_NAME[TYPE.agency]}
              </DropdownItem>
              <DropdownItem
                isActive={type === TYPE.my}
                onClick={() => handleTypeChange(TYPE.my)}
              >
                {TYPE_NAME[TYPE.my]}
              </DropdownItem>
            </DropdownContent>
          </Dropdown>

          <SearchInput
            defaultValue={listQuery}
            inputProps={{
              onClear: () => setListQuery(''),
              placeholder: 'Search by list name',
            }}
            marginLeft="auto"
            onChange={setListQuery}
          />

          <Button
            data-ignore="click-outside"
            leftIcon={<PlusIcon />}
            onClick={() => setRenderCreateItem(true)}
            trackId="foam-add-to-list-new-list"
          >
            New list
          </Button>

          {!!checkedIds.length && (
            <chakra.div display="flex" flexBasis="100%" gap={2}>
              <Dropdown>
                {({ isOpen }) => (
                  <>
                    <DropdownButton
                      sx={{
                        backgroundColor: isOpen ? 'primary.100' : 'transparent', // Custom state
                      }}
                    >
                      Selected ({checkedIds.length})
                    </DropdownButton>

                    <DropdownContent>
                      {Object.entries(state).map(([id, list]) => (
                        <DropdownItem
                          as="div"
                          key={id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            paddingY: 0,
                            svg: { marginTop: 0 },
                            _hover: {
                              backgroundColor: 'transparent',
                            },
                          }}
                        >
                          {list.name}{' '}
                          <IconButton
                            size="sm"
                            aria-label={`Remove ${list.name}`}
                            icon={<TrashIcon />}
                            variant="transparent"
                            onClick={() => deleteList(id)}
                          />
                        </DropdownItem>
                      ))}
                    </DropdownContent>
                  </>
                )}
              </Dropdown>

              <Button variant="transparent" onClick={() => clearList()}>
                Clear all
              </Button>
            </chakra.div>
          )}
        </Flex>

        {!isInitialLoading &&
        !lists.length &&
        !isLoading &&
        listQuery?.length ? (
          <EmptyResults
            height={maxListHeight.current}
            onQueryReset={() => setListQuery('')}
            query={listQuery}
          />
        ) : (
          <SavedList
            checkedIds={checkedIds}
            createItemOptions={createItemOptions}
            disabledIds={renderCreateItem ? 'all' : undefined}
            enableFooterLoader={true}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isLoading={isLoading}
            lists={lists}
            onToggleItem={handleListToggle}
            pageSize={PAGE_SIZE}
            renderCreateItem={renderCreateItem}
            styles={{ height: maxListHeight.current }}
            total={total}
          />
        )}
      </ModalBody>

      <ModalFooterDefault
        ref={modalFooterRef}
        primary={{
          ariaLabel: 'Next step',
          isDisabled: !checkedIds.length,
          onClick: () => setStep(STEP.step2),
          title: 'Next',
          trackId: 'foam-add-to-list-next-step',
        }}
        secondary={{
          ariaLabel: 'Cancel',
          onClick: onClose,
          title: 'Cancel',
        }}
      />
    </>
  );
};
